import useUtilityPrograms from '@/app/hooks/useUtilityPrograms'
import EnvironmentalChargingOptionsCard from '@/authenticated/components/ChargeSchedule/EnvironmentalChargingOptionsCard'
import { useAppSelector } from '@/hooks'
import { utilityProgramEnrollmentCollection } from '@/reducers/utilityProgramEnrollments'
import {
  selectUserCanAndHasntIntegratedUtility,
  selectUserVehicleChargerIsProgramEligible,
} from '@/selectors'
import { VehicleCharger, VehicleChargerChargeSchedule } from '@/types'
import { UtilityProgram } from '@/types/utilityProgram'
import UtilityProgramHelpers from '@/utils/utility-program-helpers/utilityProgramHelpers'
import { selectUtilityProgramForUserUtility } from '@/reducers/utilityPrograms'
import { ButtonVariant } from '@/components/Button'

export type Props = {
  vehicleCharger: VehicleCharger
  chargeSchedule: VehicleChargerChargeSchedule | undefined
  onChargeScheduleChange: (chargeSchedule: VehicleChargerChargeSchedule) => void
  buttonVariant?: ButtonVariant
}

function LegacyEnvironmentalChargingOptions(props: Props) {
  const { data: profileEnrollments } =
    utilityProgramEnrollmentCollection.useFetch()

  const potentialProgram = useAppSelector((state) =>
    selectUtilityProgramForUserUtility(state),
  ) satisfies UtilityProgram | undefined

  const profileHasEnrolledInUtilityProgram = profileEnrollments.length > 0

  const vehicleChargerIsProgramEligible = useAppSelector((state) =>
    selectUserVehicleChargerIsProgramEligible(state, props.vehicleCharger.id),
  )

  const shouldShowUtilityProgramEnrollment =
    profileHasEnrolledInUtilityProgram && vehicleChargerIsProgramEligible

  const userHasUnenrolledProgram =
    UtilityProgramHelpers.userHasUnenrolledBetterProgram(
      profileEnrollments,
      potentialProgram,
    )

  const userCanAndHasntConnectedUtility = useAppSelector(
    selectUserCanAndHasntIntegratedUtility,
  )
  return (
    <EnvironmentalChargingOptionsCard>
      {shouldShowUtilityProgramEnrollment ? (
        <EnvironmentalChargingOptionsCard.EnrollmentToggleHeader
          vehicleChargerId={props.vehicleCharger.id}
        />
      ) : (
        <EnvironmentalChargingOptionsCard.PlainHeader />
      )}
      <EnvironmentalChargingOptionsCard.AirQualityToggle
        chargeSchedule={props.chargeSchedule}
        onChange={props.onChargeScheduleChange}
      />
      {userHasUnenrolledProgram ? (
        <EnvironmentalChargingOptionsCard.ProgramEnrollmentUpsell
          chargerId={props.vehicleCharger.id}
          buttonVariant={props.buttonVariant}
        />
      ) : shouldShowUtilityProgramEnrollment ? null : userCanAndHasntConnectedUtility ? (
        <EnvironmentalChargingOptionsCard.UtilityIntegrationUpsell
          chargeSchedule={props.chargeSchedule}
          onChange={props.onChargeScheduleChange}
        />
      ) : (
        <EnvironmentalChargingOptionsCard.GridSuportToggle
          chargeSchedule={props.chargeSchedule}
          onChange={props.onChargeScheduleChange}
        />
      )}
    </EnvironmentalChargingOptionsCard>
  )
}

/**
 * A simplified environmental charging options card that has no
 * program enrollment related features that we can show for users that
 * are on multi-program.
 */
function MultiProgramEnvironmentalChargingOptions(props: Props) {
  return (
    <EnvironmentalChargingOptionsCard>
      <EnvironmentalChargingOptionsCard.PlainHeader />
      <EnvironmentalChargingOptionsCard.AirQualityToggle
        chargeSchedule={props.chargeSchedule}
        onChange={props.onChargeScheduleChange}
      />
      <EnvironmentalChargingOptionsCard.GridSuportToggle
        chargeSchedule={props.chargeSchedule}
        onChange={props.onChargeScheduleChange}
      />
    </EnvironmentalChargingOptionsCard>
  )
}

export default function ChargerEnvironmentalChargingOptions(props: Props) {
  const { allLegacyPrograms } = useUtilityPrograms({ onlyMultiPrograms: false })
  const hasLegacyPrograms = allLegacyPrograms?.length > 0
  return (
    <>
      {hasLegacyPrograms ? (
        <LegacyEnvironmentalChargingOptions {...props} />
      ) : (
        <MultiProgramEnvironmentalChargingOptions {...props} />
      )}
    </>
  )
}
